import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { PropertyForm, ZoneForm, type PropertyRow, type ZoneRow } from '../components/EntityForms';

type Prop = { id: string; name: string; property_type: string; client_id: string | null; address: string | null; area_m2: number | null; access_notes: string | null; latitude: number | null; longitude: number | null; geofence_radius_m: number };
type Zone = ZoneRow;

// Tile provider is configurable. The public OSM server is a DEV-ONLY fallback
// (its usage policy excludes heavy commercial use); in production the tile URL
// must be set explicitly — when it is empty the map renders without tiles and
// shows a "provider not configured" notice instead of loading OSM.
const TILE_URL = import.meta.env.VITE_MAP_TILE_URL
  || (import.meta.env.DEV ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' : '');
const TILE_ATTR = import.meta.env.VITE_MAP_TILE_ATTRIBUTION
  || (import.meta.env.DEV ? '© OpenStreetMap contributors (dev only)' : '');
const TILES_CONFIGURED = TILE_URL !== '';
const DEFAULT: [number, number] = [-8.5, 115.2];

export function PropertyMap() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [prop, setProp] = useState<Prop | null>(null);
  const [lat, setLat] = useState<number>(DEFAULT[0]);
  const [lng, setLng] = useState<number>(DEFAULT[1]);
  const [radius, setRadius] = useState(100);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [propFormOpen, setPropFormOpen] = useState(false);
  const [zoneFormOpen, setZoneFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);

  const mapEl = useRef<HTMLDivElement>(null);
  const refs = useRef<{ map?: any; marker?: any; circle?: any }>({});

  const loadProp = useCallback(async () => {
    if (!id) return;
    const r = await supabase.from('properties').select('id,name,property_type,client_id,address,area_m2,access_notes,latitude,longitude,geofence_radius_m').eq('id', id).single();
    const p = r.data as Prop;
    setProp(p);
    if (p?.latitude != null && p?.longitude != null) { setLat(p.latitude); setLng(p.longitude); }
    if (p?.geofence_radius_m) setRadius(p.geofence_radius_m);
  }, [id]);

  const loadZones = useCallback(async () => {
    if (!id) return;
    const r = await supabase.from('zones').select('id,name,zone_type,area_m2').eq('property_id', id).is('deleted_at', null).order('name');
    setZones((r.data as unknown as Zone[]) ?? []);
  }, [id]);

  useEffect(() => { loadProp(); loadZones(); }, [loadProp, loadZones]);

  // init leaflet (lazy) once property is loaded
  useEffect(() => {
    if (!prop || !mapEl.current) return;
    let disposed = false;
    (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (disposed || !mapEl.current) return;
      const start: [number, number] = [prop.latitude ?? DEFAULT[0], prop.longitude ?? DEFAULT[1]];
      const map = L.map(mapEl.current).setView(start, 16);
      if (TILES_CONFIGURED) L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
      const marker = L.circleMarker(start, { radius: 6, color: '#3B5BFD', fillColor: '#3B5BFD', fillOpacity: 1 }).addTo(map);
      const circle = L.circle(start, { radius: prop.geofence_radius_m ?? 100, color: '#3B5BFD', weight: 1, fillColor: '#3B5BFD', fillOpacity: 0.1 }).addTo(map);
      map.on('click', (e: any) => { setLat(e.latlng.lat); setLng(e.latlng.lng); });
      refs.current = { map, marker, circle };
    })();
    return () => { disposed = true; refs.current.map?.remove(); refs.current = {}; };
  }, [prop]);

  // reflect state on the map
  useEffect(() => {
    const { marker, circle } = refs.current;
    if (marker) marker.setLatLng([lat, lng]);
    if (circle) { circle.setLatLng([lat, lng]); circle.setRadius(radius); }
  }, [lat, lng, radius]);

  async function save() {
    if (!id) return;
    setSaving(true);
    setSaved(false);
    const { error } = await supabase.from('properties').update({ latitude: lat, longitude: lng, geofence_radius_m: radius }).eq('id', id);
    setSaving(false);
    if (!error) setSaved(true);
  }

  return (
    <Layout header={<Topbar />}>
      <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4 px-7 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{prop?.name ?? ''}</h1>
          {prop && <Button variant="secondary" onClick={() => setPropFormOpen(true)}>{t('form.editProperty')}</Button>}
        </div>
        <Card className="flex flex-wrap items-center gap-4 p-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t('map.lat')}</span>
            <span className="font-mono text-sm">{lat.toFixed(5)}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t('map.lng')}</span>
            <span className="font-mono text-sm">{lng.toFixed(5)}</span>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t('map.radius')}</span>
            <input type="number" min={10} max={2000} value={radius} onChange={(e) => setRadius(Number(e.target.value) || 0)} className="h-9 w-24 rounded-md border border-border bg-card px-2 font-mono text-sm outline-none" />
            <span className="text-muted">m</span>
          </label>
          <div className="ml-auto flex items-center gap-2">
            {saved && <span className="text-sm text-st-done-fg">{t('common.save')} ✓</span>}
            <Button variant="accent" disabled={saving} onClick={save}>{t('common.save')}</Button>
          </div>
        </Card>
        <Card className="overflow-hidden p-0">
          {!TILES_CONFIGURED && (
            <div className="border-b border-divider bg-st-onhold-bg px-4 py-2 text-xs text-st-onhold-fg">{t('map.noProvider')}</div>
          )}
          <div ref={mapEl} className="h-[460px] w-full bg-bg" />
          <div className="px-4 py-2 text-2xs text-muted">{t('map.hint')}</div>
        </Card>

        <div className="flex items-center justify-between">
          <h2 className="eyebrow">{t('form.zonesTitle')}</h2>
          <Button variant="secondary" size="sm" onClick={() => { setEditingZone(null); setZoneFormOpen(true); }}>+ {t('form.addZone')}</Button>
        </div>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {zones.length === 0 ? (
                <tr><td className="px-5 py-6 text-muted">{t('form.noZones')}</td></tr>
              ) : zones.map((z) => (
                <tr key={z.id} className="border-t border-divider first:border-0">
                  <td className="px-5 py-3 font-medium">{z.name}</td>
                  <td className="px-2 py-3 text-muted">{z.zone_type ?? '—'}</td>
                  <td className="px-2 py-3 font-mono text-muted">{z.area_m2 != null ? `${z.area_m2} m²` : '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingZone(z); setZoneFormOpen(true); }}>{t('form.edit')}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      {prop && (
        <PropertyForm
          open={propFormOpen}
          onClose={() => setPropFormOpen(false)}
          onSaved={loadProp}
          initial={prop as unknown as PropertyRow}
        />
      )}
      {id && (
        <ZoneForm open={zoneFormOpen} onClose={() => setZoneFormOpen(false)} onSaved={loadZones} initial={editingZone} propertyId={id} />
      )}
    </Layout>
  );
}
